import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { useGomakeAxios } from "@/hooks/use-gomake-axios";
import { useCustomer } from "@/hooks/use-customer";
import { selectLinesOfBusinessApi } from "@/services/api-service/lines-of-business/select-lines-api";
import { clearValidateSnapshot } from "@/services/auth/validate-cache";
import {
  ISessionLineOfBusiness,
  linesOfBusinessState,
} from "@/store/lines-of-business";
import { snackbarState } from "@/store/snackbar";

export const LINE_SELECTION_DEBOUNCE_MS = 300;

const AT_LEAST_ONE_KEY = "linesOfBusiness.atLeastOneSelected";
const SAVE_FAILED_KEY = "linesOfBusiness.selectorSaveFailed";
const NOT_ACCESSIBLE_KEY = "linesOfBusiness.selectorLineNotAccessible";

const noop = () => {};

/**
 * Header line-of-business selection: optimistic toggles, a trailing debounce that sends the
 * full desired id list, then `clearValidateSnapshot()` + `validate()` to refresh the session.
 * Requests are serialized, so the server sees writes in order and only the latest result
 * is revalidated and applied. A failure reverts to the last confirmed (Recoil) state.
 */
export const useLinesOfBusinessSelection = () => {
  const { t } = useTranslation();
  const { callApi } = useGomakeAxios();
  const { validate } = useCustomer();
  const confirmedLines = useRecoilValue(linesOfBusinessState);
  const setSnackbar = useSetRecoilState(snackbarState);

  // Desired ids while a change is pending/saving; null = nothing pending (show confirmed).
  const [pendingIds, setPendingIds] = useState<string[] | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const confirmedRef = useRef(confirmedLines);
  confirmedRef.current = confirmedLines;
  const desiredRef = useRef<string[] | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const dirtyRef = useRef(false);
  const unsyncedRef = useRef(false);
  const mountedRef = useRef(true);
  // Latest callbacks, so the long-running save loop never uses stale closures.
  const callApiRef = useRef(callApi);
  callApiRef.current = callApi;
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const showErrorRef = useRef<(key: string) => void>(noop);
  showErrorRef.current = (key: string) => {
    setErrorKey(key);
    setSnackbar({ state: true, message: t(key), type: "error" });
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const resync = useCallback(async () => {
    clearValidateSnapshot();
    await validateRef.current();
    unsyncedRef.current = false;
  }, []);

  const runSave = useCallback(async () => {
    if (inFlightRef.current) {
      dirtyRef.current = true;
      return;
    }
    inFlightRef.current = true;
    try {
      do {
        dirtyRef.current = false;
        const ids = desiredRef.current;
        if (!ids) break;
        const res: any = await selectLinesOfBusinessApi(
          callApiRef.current,
          noop,
          { lineOfBusinessIds: ids },
        );
        if (!mountedRef.current) return;
        if (!res?.success) {
          // Revert to the last confirmed state; resync first if an earlier write already landed.
          desiredRef.current = null;
          dirtyRef.current = false;
          if (unsyncedRef.current) await resync();
          if (!mountedRef.current) return;
          setPendingIds(null);
          showErrorRef.current(
            res?.code === "LINE_NOT_ACCESSIBLE"
              ? NOT_ACCESSIBLE_KEY
              : SAVE_FAILED_KEY,
          );
          return;
        }
        unsyncedRef.current = true;
        // A newer change is waiting: skip this result and send the latest list instead.
        if (dirtyRef.current) continue;
        await resync();
        if (!mountedRef.current) return;
        if (!dirtyRef.current) {
          desiredRef.current = null;
          setPendingIds(null);
        }
      } while (dirtyRef.current);
    } catch {
      if (mountedRef.current) {
        desiredRef.current = null;
        dirtyRef.current = false;
        setPendingIds(null);
        showErrorRef.current(SAVE_FAILED_KEY);
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [resync]);

  const toggleLine = useCallback(
    (lineId: string) => {
      const current =
        desiredRef.current ??
        confirmedRef.current.filter((l) => l.isSelected).map((l) => l.id);
      const isSelected = current.includes(lineId);
      if (isSelected && current.length <= 1) {
        showErrorRef.current(AT_LEAST_ONE_KEY);
        return;
      }
      const knownIds = confirmedRef.current.map((l) => l.id);
      const next = isSelected
        ? current.filter((id) => id !== lineId)
        : knownIds.filter((id) => id === lineId || current.includes(id));
      desiredRef.current = next;
      setErrorKey(null);
      setPendingIds(next);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void runSave();
      }, LINE_SELECTION_DEBOUNCE_MS);
    },
    [runSave],
  );

  const lines: ISessionLineOfBusiness[] = useMemo(
    () =>
      pendingIds
        ? confirmedLines.map((l) => ({
            ...l,
            isSelected: pendingIds.includes(l.id),
          }))
        : confirmedLines,
    [confirmedLines, pendingIds],
  );

  return {
    lines,
    selectedLines: useMemo(() => lines.filter((l) => l.isSelected), [lines]),
    isSaving: pendingIds !== null,
    errorKey,
    clearError: useCallback(() => setErrorKey(null), []),
    toggleLine,
  };
};
